import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { GAME_ID, formatMoney } from "../utils/helpers";
import { styles } from "../styles/theme";

function RoundResumePage({ currentRound, timeLeft }) {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bids, setBids] = useState({});

  useEffect(() => {
    const fetchRoundResults = async () => {
      try {
        const previousRound = currentRound - 1;
        
        // Obtener todos los jugadores
        const playersCol = collection(db, "games", GAME_ID, "players");
        const playersSnap = await getDocs(playersCol);
        
        const playersList = [];
        playersSnap.forEach((doc) => {
          playersList.push({ id: doc.id, ...doc.data() });
        });

        // Obtener las pujas de la ronda anterior
        const bidsCol = collection(db, "games", GAME_ID, "rounds", String(previousRound), "bids");
        const bidsSnap = await getDocs(bidsCol);
        
        const bidsMap = {};
        bidsSnap.forEach((doc) => {
          const bidData = doc.data();
          bidsMap[bidData.playerId] = bidData.amount;
        });

        // Ordenar jugadores: primero activos por puja descendente, luego eliminados
        playersList.sort((a, b) => {
          // Primero separar activos de eliminados
          if (a.active !== false && b.active === false) return -1;
          if (a.active === false && b.active !== false) return 1;
          
          // Entre activos, ordenar por puja descendente (mayor puja primero)
          if (a.active !== false && b.active !== false) {
            const bidA = bidsMap[a.id] || 0;
            const bidB = bidsMap[b.id] || 0;
            return bidB - bidA;
          }
          
          // Entre eliminados, también ordenar por puja
          const bidA = bidsMap[a.id] || 0;
          const bidB = bidsMap[b.id] || 0;
          return bidB - bidA;
        });

        setPlayers(playersList);
        setBids(bidsMap);
      } catch (error) {
        console.error("Error al obtener resultados de la ronda:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoundResults();
  }, [currentRound]);

  if (loading) {
    return (
      <div style={resumeStyles.page}>
        <div style={resumeStyles.card}>
          <h1 style={styles.title}>Cargando resultados...</h1>
        </div>
      </div>
    );
  }

  const activePlayers = players.filter(p => p.active !== false);
  const eliminatedPlayers = players.filter(p => p.active === false);

  return (
    <div style={resumeStyles.page}>
      <div style={resumeStyles.card}>
        <div style={resumeStyles.header}>
          <h1 style={resumeStyles.title}>📊 Resumen de la Ronda {currentRound - 1}</h1>
          <div style={resumeStyles.timer}>
            <span style={resumeStyles.timerLabel}>Siguiente ronda en:</span>
            <span style={resumeStyles.timerValue}>{timeLeft}s</span>
          </div>
        </div>

        <div style={resumeStyles.section}>
          <h2 style={resumeStyles.sectionTitle}>🍞 Compraron Pan</h2>
          <ol style={{ 
            margin: "0",
            paddingLeft: "20px",
            listStyle: "decimal",
            background: "#f8f9fa",
            borderRadius: "10px",
            padding: "20px",
            border: "1px solid #e0e0e0"
          }}>
            {activePlayers.length === 0 && (
              <li style={{ color: "#888" }}>No hay jugadores activos</li>
            )}
            {activePlayers.map((player, index) => (
              <li key={player.id} style={{ 
                marginBottom: "8px",
                color: "#333",
                fontSize: "14px"
              }}>
                <strong>{player.name}:</strong> {bids[player.id] !== undefined ? formatMoney(bids[player.id]) : "Sin oferta"}
              </li>
            ))}
          </ol>
        </div>

        {eliminatedPlayers.length > 0 && (
          <div style={resumeStyles.section}>
            <h2 style={resumeStyles.eliminatedTitle}>❌ Jugadores Eliminados</h2>
            <ol style={{ 
              margin: "0",
              paddingLeft: "20px",
              listStyle: "decimal",
              background: "#f8f8f8",
              borderRadius: "10px",
              padding: "20px",
              border: "1px solid #ddd",
              opacity: 0.7
            }}>
              {eliminatedPlayers.map((player) => (
                <li key={player.id} style={{ 
                  marginBottom: "8px",
                  color: "#999",
                  fontSize: "14px",
                  textDecoration: "line-through"
                }}>
                  <strong>{player.name}:</strong> {bids[player.id] !== undefined ? formatMoney(bids[player.id]) : "Sin oferta"}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

const resumeStyles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
  card: {
    background: "white",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    maxWidth: "900px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
    borderBottom: "3px solid #667eea",
    paddingBottom: "20px",
  },
  title: {
    fontSize: "32px",
    color: "#333",
    marginBottom: "15px",
    fontWeight: "bold",
  },
  timer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginTop: "10px",
  },
  timerLabel: {
    fontSize: "18px",
    color: "#666",
  },
  timerValue: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#667eea",
    minWidth: "60px",
    textAlign: "center",
  },
  section: {
    marginBottom: "30px",
  },
  sectionTitle: {
    fontSize: "24px",
    color: "#667eea",
    marginBottom: "15px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  eliminatedTitle: {
    fontSize: "24px",
    color: "#e74c3c",
    marginBottom: "15px",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  playerList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  playerCard: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    padding: "15px",
    background: "linear-gradient(135deg, #667eea15 0%, #764ba215 100%)",
    borderRadius: "12px",
    border: "2px solid #667eea30",
    transition: "transform 0.2s",
  },
  eliminatedCard: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
    padding: "15px",
    background: "#f8f8f8",
    borderRadius: "12px",
    border: "2px solid #ddd",
    opacity: 0.7,
  },
  playerRank: {
    fontSize: "32px",
    minWidth: "50px",
    textAlign: "center",
    fontWeight: "bold",
  },
  playerInfo: {
    flex: 1,
  },
  playerName: {
    fontSize: "20px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "8px",
  },
  eliminatedName: {
    fontSize: "20px",
    fontWeight: "bold",
    color: "#999",
    marginBottom: "8px",
    textDecoration: "line-through",
  },
  playerStats: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
  },
  stat: {
    fontSize: "16px",
    color: "#666",
    background: "white",
    padding: "5px 12px",
    borderRadius: "20px",
    border: "1px solid #ddd",
  },
};

export default RoundResumePage;
